import {
  SecurityTypesUtils,
  useSecuritiesContext,
  type ConnectionType,
  type MarketSecurityStatus,
  type SecuritiesContextProps,
  type UnifiedLiquidityToken,
} from '@talos/kyoko';
import { get } from 'lodash-es';
import { useMemo } from 'react';
import { AvailabilityEnum, type AvailabilityCondition, type AvailabilityResult } from '../types';
import {
  getMSSConditionAccountLevelAvailability,
  getMSSConditionText,
  getSymbolsToValidateForMarketFn,
  MSSConditionInfo,
  type MSSConditionSymbolLevelResult,
} from './useMSSEnabledCondition';

export type CapabilityKey = keyof NonNullable<MarketSecurityStatus['Capabilities']>;

/** A hook wrapper for getMSSCapabilityEnabledCondition */
export const useMSSCapabilityEnabledCondition = ({
  connectionType,
  capability,
  symbol,
  mssByAccountBySymbol,
  ulTokensForSymbol,
  justWarnOnFailure,
}: Omit<GetMSSCapabilityEnabledConditionParams, 'securitiesBySymbol'>): AvailabilityCondition => {
  const { securitiesBySymbol } = useSecuritiesContext();
  return useMemo(
    () =>
      getMSSCapabilityEnabledCondition({
        connectionType,
        capability,
        symbol,
        mssByAccountBySymbol,
        ulTokensForSymbol,
        justWarnOnFailure,
        securitiesBySymbol,
      }),
    [connectionType, capability, mssByAccountBySymbol, symbol, ulTokensForSymbol, securitiesBySymbol, justWarnOnFailure]
  );
};

export interface GetMSSCapabilityEnabledConditionParams {
  connectionType: ConnectionType;
  /** The capability to check */
  capability: CapabilityKey;
  /** The complete map of MSS records to base the condition checking on */
  mssByAccountBySymbol: Map<string, Map<string, MarketSecurityStatus>> | undefined;
  symbol: string | undefined;
  /** If using unified liquidity, pass the token list for the selected symbol here */
  ulTokensForSymbol?: UnifiedLiquidityToken[];
  /** If true, will just Warn instead of Disabling the item on condition failure */
  justWarnOnFailure?: boolean;
  /** Basic securitiesBySymbol ref data */
  securitiesBySymbol: SecuritiesContextProps['securitiesBySymbol'];
}

/**
 * An AvailabilityCondition which asserts that the MarketSecurityStatus.[Capability] is true.
 *
 * If no MarketSecurityStatus has been resolved and attached to the passed record, returns true.
 * If there is no Capabilities struct on the successfully resolved MarketSecurityStatus record, returns false.
 */
export function getMSSCapabilityEnabledCondition({
  connectionType,
  capability,
  mssByAccountBySymbol,
  symbol,
  ulTokensForSymbol,
  justWarnOnFailure,
  securitiesBySymbol,
}: GetMSSCapabilityEnabledConditionParams): AvailabilityCondition {
  const getSymbolsToCheck = getSymbolsToValidateForMarketFn(
    symbol,
    ulTokensForSymbol,
    securitiesBySymbol,
    connectionType
  );
  const failureAvailability = justWarnOnFailure ? AvailabilityEnum.Warning : AvailabilityEnum.Disabled;

  return {
    id: `mss-${capability}-enabled`,
    ready: mssByAccountBySymbol != null && symbol != null,
    condition: (market, marketAccount) => {
      if (!symbol) {
        // This is just a typing artefact. If there is no symbol, we will not be given any markets to validate.
        return { availability: AvailabilityEnum.Ok };
      }

      if (!mssByAccountBySymbol) {
        // If we do not receive any data map at all, resort to passing through all
        return { availability: AvailabilityEnum.Ok };
      }

      if (!marketAccount) {
        // MarketSecurityStatuses are only applicable for market accounts
        return { availability: AvailabilityEnum.Ok };
      }

      const security = securitiesBySymbol.get(symbol);
      if (SecurityTypesUtils.Compound.isProductType(security)) {
        // Compound securities do not generate MSS records, so no validation on them.
        return { availability: AvailabilityEnum.Ok };
      }

      const results: MSSConditionSymbolLevelResult[] = getSymbolsToCheck(market).map(symbol => {
        const mss = mssByAccountBySymbol.get(marketAccount.Name)?.get(symbol);
        if (!mss) {
          // If we can't find any MSS something is wrong, and we should disable the item.
          return { symbol, availability: failureAvailability, failedReason: 'No MSS found' };
        }

        const capabilitiesStruct = mss.Capabilities;
        if (!capabilitiesStruct) {
          return {
            symbol,
            availability: failureAvailability,
            failedReason: 'No capabilities',
          };
        }

        const capabilityOk = get(capabilitiesStruct, capability) === true;
        return capabilityOk
          ? { symbol, availability: AvailabilityEnum.Ok }
          : {
              symbol,
              availability: failureAvailability,
              failedReason: `${capability} disabled`,
            };
      });

      const accountLevelAvailability = getMSSConditionAccountLevelAvailability(results);

      if (accountLevelAvailability === AvailabilityEnum.Ok) {
        return { availability: AvailabilityEnum.Ok };
      }

      return {
        availability: accountLevelAvailability,
        render: () => (
          <MSSConditionInfo
            accountName={marketAccount.Name}
            selectedSymbol={symbol}
            symbolLevelResults={results}
            unifiedLiquidityEnabled={!!ulTokensForSymbol}
          />
        ),
        _text: getMSSConditionText(results),
      } satisfies AvailabilityResult;
    },
  };
}
